import { Box, Button, Modal, Typography } from '@mui/material'
import dynamic from 'next/dynamic'
import React from 'react'

import CouponModalTick from '@/src/assets/svgs/CouponModalTick'
import type { CouponSuccessModalProps } from './types'

// Dynamic import for Lottie to avoid SSR issues
const Lottie = dynamic(() => import(/* webpackChunkName: "lottie" */ 'react-lottie'), {
    ssr: false,
    loading: () => <Box />
})

const CouponSuccessModal: React.FC<CouponSuccessModalProps> = ({ open, successMessage, onClose, animationData }) => {
    // ✅ ONLY presentation logic - following CODEBASE_CONTEXT guidelines

    if (!open) return null

    const defaultOptions = {
        loop: false,
        autoplay: true,
        animationData: animationData,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice'
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    maxWidth: '400px',
                    width: '100%',
                    textAlign: 'center',
                    position: 'relative',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    outline: 'none'
                }}
            >
                {/* Lottie Animation - Positioned to cover the modal */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1,
                        pointerEvents: 'none',
                        borderRadius: '12px',
                        overflow: 'hidden'
                    }}
                    data-testid="coupon_success_modal"
                >
                    <Lottie
                        options={defaultOptions}
                        isClickToPauseDisabled={true}
                        eventListeners={[
                            {
                                eventName: 'complete',
                                callback: () => {}
                            }
                        ]}
                        speed={1}
                        height="100%"
                        width="100%"
                    />
                </div>

                {/* Success Icon - Using CouponModalTick */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 2,
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <CouponModalTick />
                </Box>

                {/* Success Message - Following reference pattern */}
                <Box sx={{ textAlign: 'center', mb: 3, position: 'relative', zIndex: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#111827" sx={{ fontSize: 16 }}>
                        {successMessage}
                    </Typography>
                </Box>

                {/* OK Button */}
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        bgcolor: '#194a8f',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: 14,
                        px: 4,
                        py: 1,
                        borderRadius: '8px',
                        textTransform: 'none',
                        position: 'relative',
                        zIndex: 2,
                        '&:hover': {
                            bgcolor: '#153a7a'
                        }
                    }}
                >
                    OK
                </Button>
            </Box>
        </Modal>
    )
}

export default CouponSuccessModal
